/**
 * RestoreBody — renders the expanded detail for a `restore` version.
 *
 * Backend audit (Commit 7 audit step):
 *   diff = {
 *     kind: 'restore',
 *     restore_target_version: number,
 *     reconstructed_from: { snapshot_version, diffs_applied },
 *     agents_affected: string[],
 *   }
 *
 *   Verified at version_service.py:761-769 (diff written) and
 *   796-804 (restore route response — same agents_affected).
 *
 *   Mock-only field NOT shipped (and omitted from this body):
 *     - agents_rollup (per-agent before/after counts) — backend
 *       ships only the agent name list, not the count grid.
 *
 * Render:
 *   - Directional hero: v{N-1} → v{target} + "rewind K version(s)"
 *     note. N is this restore version's own number; N-1 is the
 *     state immediately before the restore. target is the version
 *     we rolled back to.
 *   - agents_affected pill list (warning tone — matches the
 *     timeline node colour).
 *   - <details> disclosure with reconstructed_from snapshot
 *     version + diffs-applied count.
 */
import React from 'react';
import { VersionRecord, RestoreDiff } from '../../state/types';
import { agentDisplayName } from '../../utils/operationMeta';
import { Icon } from '../../../../components/ui/Icon';
import styles from './operationBodies.module.css';
import restoreStyles from './RestoreBody.module.css';

interface RestoreBodyProps {
  version: VersionRecord;
}

export default function RestoreBody({ version }: RestoreBodyProps) {
  const diff = version.diff as RestoreDiff | null;
  if (!diff || diff.kind !== 'restore') return null;

  const fromNumber = Math.max(0, version.version_number - 1);
  const target = diff.restore_target_version;
  const rewound = Math.max(0, fromNumber - target);
  const agents = diff.agents_affected ?? [];
  const rf = diff.reconstructed_from;

  return (
    <div className={styles.body}>
      <div className={restoreStyles.hero}>
        <span className={restoreStyles.from}>v{fromNumber}</span>
        <span className={restoreStyles.arrow} aria-hidden="true">
          <Icon name="arrow-right" size={18} />
        </span>
        <span className={restoreStyles.to}>v{target}</span>
        {rewound > 0 && (
          <span className={restoreStyles.note}>
            rewind {rewound} version{rewound === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {agents.length > 0 && (
        <>
          <div className={styles.subHeading}>
            {agents.length} agent{agents.length === 1 ? '' : 's'} affected
          </div>
          <div className={restoreStyles.agents}>
            {agents.map((a) => (
              <span key={a} className={restoreStyles.agentChip}>
                {agentDisplayName(a)}
              </span>
            ))}
          </div>
        </>
      )}

      {rf && (
        <details className={restoreStyles.disclosure}>
          <summary>Reconstruction details</summary>
          <div className={restoreStyles.disclosureBody}>
            Reconstructed from snapshot <code>v{rf.snapshot_version}</code>{' '}
            with <code>{rf.diffs_applied}</code> forward diff
            {rf.diffs_applied === 1 ? '' : 's'} re-applied.
          </div>
        </details>
      )}
    </div>
  );
}
